"use client";

import { useState } from "react";
import { BellRing, Calendar } from "lucide-react";
import { useTranslation } from "@sajilni/localization";
import { useSession } from "@sajilni/auth";
import { usePubNubNotifications } from "@/hooks/usePubNubNotifications";
import {
  Button,
  Badge,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "ui";
import { useHeaderAccentColor } from "@/hooks/use-header-accent-color";
import type { CSSProperties } from "react";
import {
  NotificationList,
  useNotificationInbox,
} from "./notification-inbox";

export { getSenderInfo, getReceiverInfo } from "./notification-inbox";

export default function NotificationPopup() {
  const { t } = useTranslation("notification");
  const { accentColor } = useHeaderAccentColor();
  const [open, setOpen] = useState(false);
  const { data: session } = useSession();
  const inbox = useNotificationInbox(open);
  usePubNubNotifications(session?.user?.id, inbox.applyLiveNotification);

  const popoverAccentStyle = (accentColor
    ? {
        "--primary-500": accentColor,
        "--primary-600": accentColor,
        "--primary": accentColor,
      }
    : undefined) as CSSProperties | undefined;

  const { unreadCount, hasUnreadMeetings, markAllAsRead } = inbox;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && unreadCount > 0) {
      void markAllAsRead();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative rounded-full p-0"
          aria-label="Notifications"
        >
          <BellRing className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="danger"
              className="absolute -end-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full p-0 text-[10px]"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="!w-[min(100vw-1rem,22rem)] overflow-hidden p-0 sm:!w-96 md:!w-[26rem]"
        align="end"
        sideOffset={8}
        style={popoverAccentStyle}
      >
        <Card className="!border-0 !shadow-none">
          <CardHeader className="border-b !px-3 !py-2.5 sm:!px-4">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-start text-sm font-semibold sm:text-base">
                {t("Notifications")}
              </CardTitle>
              <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                {hasUnreadMeetings && (
                  <Badge variant="danger" className="text-xs font-normal">
                    <Calendar className="me-1 h-3 w-3" />
                    {t("Meetings")}
                  </Badge>
                )}
                {unreadCount > 0 && (
                  <Badge variant="outlined" className="text-xs font-normal">
                    {unreadCount} {t("unread")}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="!p-0">
            <NotificationList inbox={inbox} variant="popover" />
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
}
